import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChartLineUp, Users, FileArrowUp, Trophy, Medal, CurrencyDollar,
  Airplane, Warning, ChatsCircle, ClipboardText, UserGear, ClipboardText as ClipboardChecker,
  SignOut, Database, PlugsConnected, Gear, BookOpen, ChartBar,
} from "@phosphor-icons/react";
import { useAuth, can, ROLE_LABELS } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLineUp, roles: "all" },
  { to: "/registrations", label: "Registrations", icon: ClipboardChecker, roles: ["super_admin", "admin_ops"] },
  { to: "/participants", label: "Participants", icon: Users, roles: ["super_admin", "admin_ops", "sales_ib"] },
  { to: "/uploads", label: "Upload Report", icon: FileArrowUp, roles: ["super_admin", "admin_ops"] },
  { to: "/imports", label: "Import History", icon: Database, roles: ["super_admin", "admin_ops", "broker_viewer"] },
  { to: "/integrations", label: "Broker Integrations", icon: PlugsConnected, roles: ["super_admin", "admin_ops", "broker_viewer"] },
  { to: "/broker-docs", label: "Webhook Docs", icon: BookOpen, roles: ["super_admin", "admin_ops", "broker_viewer"] },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, roles: "all" },
  { to: "/milestones", label: "Milestones", icon: Medal, roles: "all" },
  { to: "/payouts", label: "Rewards & Payouts", icon: CurrencyDollar, roles: ["super_admin", "admin_ops"] },
  { to: "/bali-qualifiers", label: "Bali Qualifiers", icon: Airplane, roles: "all" },
  { to: "/risk", label: "Risk Monitoring", icon: Warning, roles: ["super_admin", "admin_ops"] },
  { to: "/crm", label: "CRM Follow-up", icon: ChatsCircle, roles: ["super_admin", "admin_ops", "sales_ib"] },
  { to: "/audit", label: "Audit Log", icon: ClipboardText, roles: ["super_admin", "admin_ops"] },
  { to: "/users", label: "User Management", icon: UserGear, roles: ["super_admin"] },
  { to: "/analytics", label: "Website Analytics", icon: ChartBar, roles: ["super_admin"] },
  { to: "/settings", label: "Campaign Settings", icon: Gear, roles: ["super_admin"] },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const visible = NAV.filter((n) => n.roles === "all" || n.roles.includes(user?.role));

  return (
    <aside className="h-full w-full bg-[#0B0F19] border-r border-white/10 flex flex-col">
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_mbe-bali-tracker/artifacts/8ljitr27_PROFILE%20ALL%20CHANNEL-05.jpg"
            alt="MBE International"
            className="w-11 h-11 rounded-full object-cover ring-2 ring-orange-500/40"
          />
          <div>
            <p className="font-display text-sm font-bold text-white leading-tight">MBE Road</p>
            <p className="font-display text-sm font-bold text-orange-400 leading-tight">To Bali</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {visible.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.to.replace("/", "")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150",
                isActive
                  ? "bg-blue-500/15 text-white border border-blue-500/30"
                  : "text-gray-400 hover:text-white hover:bg-white/5 border border-transparent",
              )
            }
          >
            <item.icon weight="duotone" size={18} />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-3 pb-2">
        <button
          data-testid="download-manual-btn"
          onClick={async () => {
            try {
              const { data: blob } = await (await import("@/lib/api")).api.get("/docs/user-manual.pdf", { responseType: "blob" });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "MBE_Bali_User_Manual.pdf";
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch { /* silent */ }
          }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs text-gray-400 hover:text-white hover:bg-white/5 border border-white/5 transition-colors"
        >
          <BookOpen weight="duotone" size={14} />
          <span>User Manual (PDF)</span>
        </button>
      </div>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-white truncate">{user?.name || user?.email}</p>
            <p className="text-[10px] text-gray-500 truncate">{ROLE_LABELS[user?.role]}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="text-gray-400 hover:text-red-400 transition-colors p-1.5 rounded-md hover:bg-white/5"
            title="Logout"
          >
            <SignOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
